'use client'

import { useSiteConfig } from '@/feature/config/hooks/useSiteConfig.client'
import { ProductsStickersDrawer } from '@/feature/product-stickers/components/products-stickers-drawer/ProductsStickersDrawer.client'
import { buildProductQrCodeUrl } from '@/feature/product-stickers/services/_services/products-stickers.qr-url'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { Button } from '@/ui/buttons/button/Button.client'
import { Drawer, DrawerBody, DrawerFooter } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { ProductBarCode } from '@/ui/product-bar-code/ProductBarCode'
import { getCookie } from 'cookies-next'
import { useParams } from 'next/navigation'

type ProductQrCodeDrawerProps = {
  productReference: string
  productName: string
  onProductQrCodeDrawerOpen: () => void
  onProductsStickersDrawerOpen: () => void
  onProductsStickersDownloadSuccess: (stickersPerSheet: number) => void
}

export function ProductQrCodeDrawer({
  productReference,
  productName,
  ...otherProps
}: ProductQrCodeDrawerProps) {
  const {
    onProductQrCodeDrawerOpen,
    onProductsStickersDrawerOpen,
    onProductsStickersDownloadSuccess,
  } = otherProps

  const { t } = useTranslation(['productQrCodeDrawer'])
  const { config } = useSiteConfig()
  const { slug } = useParams()
  const isClientHydrated = useNextIsClientHydrated()
  // useSiteConfig() is skipped during SSR, so its value is only known client-side.
  // Stay hidden until hydrated to keep the first paint consistent with the server.
  const isProductLabelGenerationEnabled =
    isClientHydrated && config?.catalog?.isProductLabelGenerationEnabled

  if (!isProductLabelGenerationEnabled) {
    return null
  }

  const url = buildProductQrCodeUrl({
    siteOrigin: window.location.origin,
    urlKey: Array.isArray(slug) ? slug.join('/') : slug,
    reference: productReference,
    subsidiaryId: getCookie('subsidiary-id')?.toString(),
    companyCode: getCookie('company-code')?.toString(),
  })

  return (
    <Drawer
      size="sm"
      title={t('productQrCodeDrawer:heading')}
      trigger={
        <Button
          isRound
          variant="tertiary"
          size="sm"
          iconLeft={<Icon.QrCode />}
          onPress={onProductQrCodeDrawerOpen}
        >
          {t('productQrCodeDrawer:triggerLabel')}
        </Button>
      }
    >
      <DrawerBody>
        <ProductBarCode
          className="mt-1"
          url={url}
          reference={t('productQrCodeDrawer:productReference', { reference: productReference })}
          label={t('productQrCodeDrawer:scanMeTitle')}
          qrCodeTitle={t('productQrCodeDrawer:scanMeTitle')}
        />
      </DrawerBody>
      <DrawerFooter>
        <ProductsStickersDrawer
          triggerVariant="button"
          references={[productReference]}
          filenameDetails={`${productReference}-${productName}`}
          onOpen={onProductsStickersDrawerOpen}
          onDownloadSuccess={onProductsStickersDownloadSuccess}
        />
      </DrawerFooter>
    </Drawer>
  )
}
